import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";

const SERVICE_COST_RULE_CATEGORIES = ["Translation", "Interpretation", "Other"];

const fallbackRule = {
  IsEnabled: true,
  DefaultIncludeInTotalCost: true,
  DefaultIncludeInAccountCharge: false,
};

const ServiceCostTypeRulesEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { overlayData, setOverlayName, showSnackbar, handleErrorDialog } = useStateContext();

  const costTypeId = overlayData?.costTypeId;
  const costTypeName = overlayData?.costTypeName || "Service Cost Type";
  const defaultIncludeInTotalCost = overlayData?.defaultIncludeInTotalCost !== false;
  const defaultIncludeInAccountCharge = Boolean(overlayData?.defaultIncludeInAccountCharge);

  usePageTitle("Edit Service Cost Type Rules");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const [defaultVisibleToLinguists, setDefaultVisibleToLinguists] = useState(
    Boolean(overlayData?.visibleToLinguists)
  );

  const closeOverlay = () => setOverlayName("");

  useEffect(() => {
    let isMounted = true;

    const loadRules = async () => {
      if (!costTypeId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(`/serviceCosts/types/${costTypeId}/rules`);
        const rows = Array.isArray(response.data) ? response.data : [];

        const nextDraft = SERVICE_COST_RULE_CATEGORIES.reduce((acc, category) => {
          const matched = rows.find((item) => item.ServiceCategory === category);
          acc[category] = {
            IsEnabled: matched?.IsEnabled ?? fallbackRule.IsEnabled,
            DefaultIncludeInTotalCost:
              matched?.DefaultIncludeInTotalCost ?? defaultIncludeInTotalCost,
            DefaultIncludeInAccountCharge:
              matched?.DefaultIncludeInAccountCharge ?? defaultIncludeInAccountCharge,
            VisibleToLinguists: matched?.VisibleToLinguists ?? defaultVisibleToLinguists,
          };
          return acc;
        }, {});

        if (isMounted) {
          setDraft(nextDraft);
        }
      } catch (error) {
        if (isMounted) {
          handleErrorDialog(
            error?.response?.data?.error || "Unable to load service cost category rules",
            theme,
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRules();
    return () => {
      isMounted = false;
    };
  }, [
    costTypeId,
    defaultIncludeInAccountCharge,
    defaultIncludeInTotalCost,
    handleErrorDialog,
    theme,
  ]);

  const updateRule = (category, field, value) => {
    setDraft((prev) => ({
      ...prev,
      [category]: {
        ...(prev[category] || fallbackRule),
        [field]: value,
      },
    }));
  };

  const updateRuleVisibility = (category, value) => {
    setDraft((prev) => ({
      ...prev,
      [category]: {
        ...(prev[category] || fallbackRule),
        VisibleToLinguists: value,
      },
    }));
  };

  const emitSettingsSaved = (recordId, changeAction) => {
    if (!socket || !currentUser?.id) return;

    socket.emit("settings:saved", {
      settingsType: "ServiceCostType",
      recordId,
      changeAction,
      user: {
        id: currentUser.id,
        name: currentUser.Name,
        imageThumbnail: currentUser.ImageThumbnail,
      },
    });
  };

  const handleSave = async () => {
    if (!costTypeId) {
      showSnackbar("Missing cost type reference", theme, "error");
      return;
    }

    setSaving(true);
    try {
      await Promise.all(
        SERVICE_COST_RULE_CATEGORIES.map((category) =>
          api.put(`/serviceCosts/types/${costTypeId}/rules/${category}`, {
            IsEnabled: Boolean(draft?.[category]?.IsEnabled),
            DefaultIncludeInTotalCost: Boolean(
              draft?.[category]?.DefaultIncludeInTotalCost,
            ),
            DefaultIncludeInAccountCharge: Boolean(
              draft?.[category]?.DefaultIncludeInAccountCharge,
            ),
            VisibleToLinguists: Boolean(draft?.[category]?.VisibleToLinguists),
          }),
        ),
      );

      emitSettingsSaved(costTypeId, "Updated");
      showSnackbar("Category rules updated", theme, "success");
      if (typeof overlayData?.onSaved === "function") {
        await overlayData.onSaved();
      }
      closeOverlay();
    } catch (error) {
      handleErrorDialog(
        error?.response?.data?.error || "Unable to update service cost category rules",
        theme,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={closeOverlay}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "860px",
          maxHeight: "80%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              Category Rules: {costTypeName}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <Tooltip title={isMobile && saving ? "Saving..." : ""} placement="top">
              <span>
                {isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || loading}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                ) : (
                  <Button
                    loading={saving}
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={saving || loading}
                    sx={{ borderRadius: "12px" }}
                  >
                    Save
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            SERVICE_COST_RULE_CATEGORIES.map((category) => {
              const rule = draft[category] || fallbackRule;

              return (
                <Box
                  key={category}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "10px",
                    px: 1.25,
                    py: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body1" fontWeight={700} sx={{ mb: 0.5 }}>
                    {category}
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(rule.IsEnabled)}
                        onChange={(event) =>
                          updateRule(category, "IsEnabled", event.target.checked)
                        }
                      />
                    }
                    label="Enabled for this category"
                    sx={{
                      width: "100%",
                      m: 0,
                      pl: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(rule.DefaultIncludeInTotalCost)}
                        onChange={(event) =>
                          updateRule(
                            category,
                            "DefaultIncludeInTotalCost",
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label="Default: include in Total Cost"
                    sx={{
                      width: "100%",
                      m: 0,
                      pl: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(rule.DefaultIncludeInAccountCharge)}
                        onChange={(event) =>
                          updateRule(
                            category,
                            "DefaultIncludeInAccountCharge",
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label="Default: include in Account Charge"
                    sx={{
                      width: "100%",
                      m: 0,
                      pl: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  />
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceCostTypeRulesEdit;
